import {
  Box,
  Collapse,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, MutableRefObject, useCallback, useRef, useState } from "react";
import * as TinyMCETypes from "tinymce";
import { ErrorMessage, RequiredIndicator } from "../../../App/Components/Forms/formHelpers";
import { TemplateFieldType } from "../../../App/Enums/TemplateFieldType";
import { useDebouncedEffect } from "../../../App/State/Hooks/useDebouncedEffect";
import { useDebouncedHandler } from "../../../App/State/Hooks/useDebouncedHandler";
import { IEmailInput } from "../../../App/State/Slices/MessageDialogInputSlice";
import { messageContainsLinkNote } from "../../../MessageComponents/Constants/MessageConstants";
import { Functions } from "../../../MessageComponents/EmailComponents/Functions";
import { Plugins } from "../../../MessageComponents/EmailComponents/Plugins";
import { TinyEditorUncontrolled } from "../../../MessageComponents/EmailComponents/TinyEditor";
import { Toolbar } from "../../../MessageComponents/EmailComponents/Toolbar";
import MessageTemplateFields from "../../../MessageComponents/SendGroupMessage/Components/MessageTemplateFields/MessageTemplateFields";
import { TextMessageErrorMessaging } from "../../../MessageComponents/SendGroupMessage/Components/SharedComponents/TextMessageInput";
import {
  maxSubjectLength,
  selectEditEmailBody,
  selectEditName,
  selectEditSubject,
  selectEditTextBody,
  selectEditVisibility,
  useDraftMessageTemplateStore,
} from "./useMessageTemplateStore";

export const InputName = ({ initialValue, isLoading }: { initialValue: string; isLoading: boolean }) => {
  const { nameErrorMessage, nameTouched, editDraft } = useDraftMessageTemplateStore(selectEditName);
  const [localInput, setLocalInput] = useState(initialValue);
  const [localTouched, setLocalTouched] = useState(nameTouched);
  const handleInputTemplateName = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setLocalTouched(true);
    setLocalInput(e.target.value);
  };
  useDebouncedEffect(
    () => {
      if (localTouched) {
        editDraft({ name: localInput.trim() });
      }
    },
    [localInput, localTouched, editDraft],
    200,
  );
  return (
    <>
      <Typography sx={{ mt: 0, mb: 0 }} variant="h6" component={FormLabel} htmlFor="template-name">
        Template Name
        <RequiredIndicator />
      </Typography>
      <FormControl fullWidth>
        <TextField
          id="template-name"
          data-test="template-name-input"
          multiline
          maxRows={4}
          onChange={handleInputTemplateName}
          value={localInput}
          disabled={isLoading}
          error={!!nameErrorMessage && nameTouched}
        />
        <Collapse in={!!nameErrorMessage && nameTouched} unmountOnExit>
          <ErrorMessage
            message={nameErrorMessage || ""}
            sx={{ display: "block" }}
            id="template-name-error-message"
          />
        </Collapse>
      </FormControl>
    </>
  );
};

export const InputVisibility = ({ isLoading }: { isLoading: boolean }) => {
  const { visibility, editDraft } = useDraftMessageTemplateStore(selectEditVisibility);
  return (
    <>
      <Typography sx={{ mt: 0, mb: 0 }} variant="h6" component="div">
        Visibility Status
      </Typography>
      <FormControl fullWidth>
        <Select
          id="Visibility"
          data-test="template-visibility-select"
          value={visibility}
          disabled={isLoading}
          onChange={(e) => editDraft({ visibility: e.target.value as "Public" | "Private" })}
        >
          <MenuItem value={"Public"}>Public</MenuItem>
          <MenuItem value={"Private"}>Private (Only Visible to You)</MenuItem>
        </Select>
      </FormControl>
    </>
  );
};

export const InputSubject = ({ initialValue, isLoading }: { initialValue: string; isLoading: boolean }) => {
  const { subjectErrorMessage, subjectTouched, editDraft } = useDraftMessageTemplateStore(selectEditSubject);
  const [localInput, setLocalInput] = useState(initialValue);
  const [localTouched, setLocalTouched] = useState(subjectTouched);
  const handleInputEmailSubject = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setLocalTouched(true);
    setLocalInput(e.target.value);
  };
  useDebouncedEffect(
    () => {
      if (localTouched) {
        editDraft({ subject: localInput.trim() });
      }
    },
    [localInput, localTouched, editDraft],
    200,
  );
  return (
    <>
      <Typography sx={{ mt: 0, mb: 0 }} variant="h6" component={FormLabel} htmlFor="email-subject">
        Subject
        <RequiredIndicator />
      </Typography>
      <TextField
        id="email-subject"
        name="email-subject"
        data-test="email-subject-input"
        aria-describedby="subject-error-message"
        onChange={handleInputEmailSubject}
        value={localInput}
        disabled={isLoading}
        color={!subjectErrorMessage ? "primary" : "error"}
        sx={{ width: 1 }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Collapse in={!!subjectErrorMessage && subjectTouched} unmountOnExit>
          <ErrorMessage
            message={subjectErrorMessage || ""}
            sx={{ display: "block" }}
            id="subject-error-message"
          />
        </Collapse>
        <Typography
          variant="caption"
          sx={{
            fontStyle: "italic",
            color: subjectErrorMessage && subjectTouched ? "error.main" : "primary",
            ml: "auto",
          }}
        >
          {localInput.length || 0}/{maxSubjectLength}
        </Typography>
      </Box>
    </>
  );
};

export const InputEmailBody = ({ initialValue, isLoading }: { initialValue: string; isLoading: boolean }) => {
  const { body, bodyErrorMessage, bodyTouched, editDraft } =
    useDraftMessageTemplateStore(selectEditEmailBody);
  const editorRef = useRef<TinyMCETypes.Editor | null>(null);

  const handleInputEmailBody = useDebouncedHandler(
    (value: IEmailInput, editorRef?: MutableRefObject<TinyMCETypes.Editor | null>, insert?: boolean) => {
      editDraft({
        body: value.body,
        bodyText: value.bodyText,
      });
      if (editorRef?.current) {
        if (insert) {
          editorRef.current.insertContent(value.body);
        } else {
          editorRef.current.setContent(value.body);
        }
      }
    },
    200,
  );

  const onEditorChange = useCallback(
    (newText: string) => {
      // Use the text version of the body for displaying and validating the length of the text only
      if (!editorRef.current) {
        return;
      }
      // Raw HTML is used for the actual message body which is sent to the server
      const input: IEmailInput = {
        body: newText,
        bodyText: editorRef.current.getContent({ format: "text" }).trim(),
      };
      handleInputEmailBody(input);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorRef.current],
  );

  return (
    <Box
      sx={(theme) => ({
        '& div[role="application"]': {
          borderColor: body.length === 0 && bodyTouched ? theme.palette.error.main : "#eee",
        },
        ".tox .tox-dialog-wrap__backdrop": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      })}
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography sx={{ mt: 0, mb: 0 }} variant="h6" component={FormLabel} htmlFor="email-body">
          Body
          <RequiredIndicator />
        </Typography>
        <MessageTemplateFields
          excludedTypes={[TemplateFieldType.Forms]}
          handleTemplateButtonClick={(_ev, field) => {
            handleInputEmailBody(
              {
                body: `[[${field}]]`,
                bodyText: `[[${field}]]`,
              } as IEmailInput,
              editorRef,
              true,
            );
          }}
        />
      </Stack>
      <TinyEditorUncontrolled
        defaultValue={initialValue}
        editorRef={editorRef}
        textareaName="email-body"
        plugins={Plugins}
        toolbar={Toolbar}
        functions={Functions}
        disabled={isLoading}
        onEditorChange={onEditorChange}
      />
      <Box
        sx={{
          gap: 1,
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-end", md: "center" },
          justifyContent: "space-between",
        }}
      >
        <Collapse in={!!bodyErrorMessage && bodyTouched} unmountOnExit>
          <ErrorMessage message={bodyErrorMessage || ""} sx={{ display: "block" }} id="body-error-message" />
        </Collapse>
        {/* CRM-485 Remove email length validation until Large Email Templates are implemented */}
        {/* <Typography
          variant="caption"
          sx={{
            fontStyle: "italic",
            color: bodyErrorMessage && bodyTouched ? "error.main" : "primary",
            ml: { xs: 0, md: "auto" },
          }}
        >
          {bodyText?.length}/{maxEmailLength}
        </Typography> */}
      </Box>
    </Box>
  );
};

export const InputTextBody = ({ initialValue, isLoading }: { initialValue: string; isLoading: boolean }) => {
  const { bodyErrorMessage, bodyTouched, editDraft, textSplitCount, textSplitWarning, textContainsLink } =
    useDraftMessageTemplateStore(selectEditTextBody);
  const [localInput, setLocalInput] = useState(initialValue);
  const [localTouched, setLocalTouched] = useState(bodyTouched);

  const handleInputBody = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setLocalTouched(true);
    setLocalInput(e.target.value);
  };

  const handleTemplateButtonClick = (_ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, field: string) => {
    setLocalTouched(true);
    setLocalInput((val) => val + ` [[${field}]]`);
  };

  useDebouncedEffect(
    () => {
      if (localTouched) {
        editDraft({ body: localInput.trim() });
      }
    },
    [localInput, initialValue, editDraft],
    300,
  );

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Typography sx={{ mt: 0, mb: 0 }} variant="h6" component={FormLabel} htmlFor="email-body">
          Body
          <RequiredIndicator />
        </Typography>
        <MessageTemplateFields
          excludedTypes={[TemplateFieldType.Email, TemplateFieldType.Forms]}
          handleTemplateButtonClick={handleTemplateButtonClick}
        />
      </Stack>
      <Box>
        <TextField
          data-test="text-body-input"
          multiline
          fullWidth
          aria-describedby="text-input-error"
          id="inputTextMessageTemplateBody"
          name="inputTextMessageTemplateBody"
          placeholder="Write a message"
          minRows={3}
          onChange={handleInputBody}
          value={localInput}
          disabled={isLoading}
          color={bodyErrorMessage && bodyTouched ? "error" : "primary"}
        />
        <TextMessageErrorMessaging
          inputTextMessage={localInput ?? initialValue}
          bodyErrorMessage={bodyTouched ? bodyErrorMessage : undefined}
          textSplitWarning={textSplitWarning}
          textSplitCount={textSplitCount}
          textContainsLinkMessage={textContainsLink ? messageContainsLinkNote : undefined}
        />
      </Box>
    </>
  );
};
